import ReactApexChart from "react-apexcharts";
import WidgetSkeleton from "../widget/WidgetSkeleton";
import { ApexOptions } from "apexcharts";

type Props = {
  deliveryStatusCheckpoints: any;
  timestampCheckpoints: any;
  avgWeightByService: any;
  avgPiecesByService: any;
  avgWeightPerPieces: any;
  isLoadingDSCheckpoints: boolean;
  isLoadingTimestampCheckpoints: boolean;
  isLoadingAvgWeightByService: boolean;
  isLoadingAvgPiecesByService: boolean;
  isLoadingAvgWeightPerPieces: boolean;
};

const Section7 = ({
  deliveryStatusCheckpoints,
  timestampCheckpoints,
  isLoadingDSCheckpoints,
  isLoadingTimestampCheckpoints,
  avgWeightByService,
  avgPiecesByService,
  avgWeightPerPieces,
  isLoadingAvgWeightByService,
  isLoadingAvgPiecesByService,
  isLoadingAvgWeightPerPieces,
}: Props) => {
  const safeAvgWeightPerPieces = avgWeightPerPieces || {
    labels: [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ],
    avgWeightPerPiecesPremium: [
      { name: "HIT", data: Array(12).fill(0) },
      { name: "JET", data: Array(12).fill(0) },
    ],
    avgWeightPerPiecesRegular: [
      { name: "TREX", data: Array(12).fill(0) },
      { name: "GODA", data: Array(12).fill(0) },
    ],
  };

  const dummyChart = {
    labels: [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ],
    datasets: [
      {
        name: "Default",
        data: Array(12).fill(0),
        backgroundColor: "#cccccc",
      },
    ],
  };

  const createHorizontalBarChartOptions = (chartData: any): ApexOptions => {
    const allValues = chartData.datasets.flatMap(
      (dataset: any) => dataset.data
    );

    return {
      series: chartData.datasets.map((dataset: any) => ({
        name: dataset.name,
        data: dataset.data,
      })),
      chart: {
        type: "bar",
        height: 1102,
        stacked: false,
        toolbar: {
          show: false,
        },
      },
      colors: ["#608750", "#e9a299", "#440e0e"],
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: "right",
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 1,
        colors: ["#fff"],
      },
      grid: {
        xaxis: {
          lines: {
            show: false,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "8px",
            colors: "#000000",
          },
        },
      },
      xaxis: {
        categories: chartData.labels,
        min: 0,
        max: 100, // Menggunakan max: 100 karena ini adalah persentase
        tickAmount: 4,
        labels: {
          formatter: function (val: string | number) {
            return val + "%";
          },
          style: {
            colors: "#000000",
          },
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "center",
        offsetY: 10,
      },
    };
  };

  const createLineChartOptions = (chartData: any): ApexOptions => {
    const allValues = chartData.datasets.flatMap(
      (dataset: any) => dataset.data
    );
    const maxValue = Math.max(...allValues);

    // Tentukan pembulatan kelipatan
    const getRoundFactor = (num: number) => {
      if (num <= 100) return 10;
      if (num <= 1000) return 100;
      if (num <= 5000) return 500;
      if (num <= 20000) return 1000;
      if (num <= 50000) return 5000;
      return 10000;
    };

    const roundFactor = getRoundFactor(maxValue);
    const yMax = Math.ceil(maxValue / roundFactor) * roundFactor;

    return {
      series: chartData.datasets.map((dataset: any) => ({
        name: dataset.name,
        data: dataset.data,
      })),
      chart: {
        type: "line",
        height: 300,
        toolbar: {
          show: false,
        },
      },
      stroke: {
        width: 3,
        curve: "smooth",
      },
      colors: chartData.datasets.map((dataset: any) => dataset.backgroundColor),
      xaxis: {
        categories: chartData.labels,
        labels: {
          style: {
            fontSize: "12px",
            colors: "#000000",
          },
        },
      },
      yaxis: {
        min: 0,
        max: yMax,
        tickAmount: 6,
        labels: {
          formatter: (value: number) =>
            Math.round(value).toLocaleString("id-ID"),
          style: {
            colors: "#000000",
          },
        },
      },
      tooltip: {
        y: {
          formatter: (value: number) => "Rp. " + value.toLocaleString("id-ID"),
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "center",
        fontSize: "14px",
      },
      grid: {
        borderColor: "#e0e0e0",
        strokeDashArray: 0,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      markers: {
        size: 0,
      },
    };
  };

  return (
    <div className="row mb-3">
      <div className="col-12 col-lg-6 mb-4 mb-lg-0">
        <div className="row">
          <div className="col-12 col-sm-6 mb-4 mb-sm-0">
            <div className="card bg-white p-4" style={{ position: "relative" }}>
              {isLoadingDSCheckpoints && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "white",
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WidgetSkeleton />
                </div>
              )}

              <div className="row mb-2">
                <div className="col-12">
                  <div className="flex justify-between">
                    <span className="text-black">
                      PERFORMANCE DELIVERY STATUS CHECK POINT
                    </span>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div style={{ maxHeight: "1102px", minHeight: "1102px" }}>
                    <ReactApexChart
                      options={createHorizontalBarChartOptions(
                        deliveryStatusCheckpoints || {
                          labels: ["City"],
                          datasets: [{ name: "City", data: [0] }],
                        }
                      )}
                      series={
                        createHorizontalBarChartOptions(
                          deliveryStatusCheckpoints || {
                            labels: ["City"],
                            datasets: [{ name: "City", data: [0] }],
                          }
                        ).series
                      }
                      type="bar"
                      height={1102}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6">
            <div className="card bg-white p-4" style={{ position: "relative" }}>
              {isLoadingTimestampCheckpoints && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "white",
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WidgetSkeleton />
                </div>
              )}

              <div className="row mb-2">
                <div className="col-12">
                  <div className="flex justify-between">
                    <span className="text-black">
                      PERFORMANCE TIME STAMP CHECK POINT
                    </span>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div style={{ maxHeight: "1102px", minHeight: "1102px" }}>
                    <ReactApexChart
                      options={createHorizontalBarChartOptions(
                        timestampCheckpoints || {
                          labels: ["City"],
                          datasets: [{ name: "City", data: [0] }],
                        }
                      )}
                      series={
                        createHorizontalBarChartOptions(
                          timestampCheckpoints || {
                            labels: ["City"],
                            datasets: [{ name: "City", data: [0] }],
                          }
                        ).series
                      }
                      type="bar"
                      height={1102}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-6">
        <div className="row mb-4">
          <div className="col-12 col-sm-6 mb-4 mb-sm-0">
            <div className="card bg-white p-4">
              {isLoadingAvgWeightByService && <WidgetSkeleton />}
              <div className="text-gray-500 text-md mb-3">
                <span className="text-black fs-sm">
                  AVG. REVENUE PER WEIGHT by Premium Service
                </span>
              </div>
              <div
                className="flex justify-between"
                style={{ height: "300px", width: "100%" }}
              >
                <div style={{ width: "100%" }}>
                  <ReactApexChart
                    options={createLineChartOptions(
                      avgWeightByService?.avgRevenueWeight || dummyChart
                    )}
                    series={
                      createLineChartOptions(
                        avgWeightByService?.avgRevenueWeight || dummyChart
                      ).series
                    }
                    type="line"
                    height={300}
                    width="100%"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 mb-4 mb-sm-0">
            <div className="card bg-white p-4">
              {isLoadingAvgWeightByService && <WidgetSkeleton />}
              <div className="text-gray-500 text-md mb-3">
                <span className="text-black fs-sm">
                  AVG. REVENUE PER WEIGHT by Regular Service
                </span>
              </div>
              <div
                className="flex justify-between"
                style={{ height: "300px", width: "100%" }}
              >
                <div style={{ width: "100%" }}>
                  <ReactApexChart
                    options={createLineChartOptions(
                      avgWeightByService?.avgRevenueWeightRegular || dummyChart
                    )}
                    series={
                      createLineChartOptions(
                        avgWeightByService?.avgRevenueWeightRegular ||
                          dummyChart
                      ).series
                    }
                    type="line"
                    height={300}
                    width="100%"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row mb-4">
          <div className="col-12 col-sm-6 mb-4 mb-sm-0">
            <div className="card bg-white p-4">
              {isLoadingAvgPiecesByService && <WidgetSkeleton />}
              <div className="text-gray-500 text-md mb-3">
                <span className="text-black fs-sm">
                  AVG. REVENUE PER PIECES by Premium Service
                </span>
              </div>
              <div
                className="flex justify-between"
                style={{ height: "300px", width: "100%" }}
              >
                <div style={{ width: "100%" }}>
                  <ReactApexChart
                    options={createLineChartOptions(
                      avgPiecesByService?.avgRevenuePieces || dummyChart
                    )}
                    series={
                      createLineChartOptions(
                        avgPiecesByService?.avgRevenuePieces || dummyChart
                      ).series
                    }
                    type="line"
                    height={300}
                    width="100%"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 mb-4 mb-sm-0">
            <div className="card bg-white p-4">
              {isLoadingAvgPiecesByService && <WidgetSkeleton />}
              <div className="text-gray-500 text-md mb-3">
                <span className="text-black fs-sm">
                  AVG. REVENUE PER PIECES by Regular Service
                </span>
              </div>
              <div
                className="flex justify-between"
                style={{ height: "300px", width: "100%" }}
              >
                <div style={{ width: "100%" }}>
                  <ReactApexChart
                    options={createLineChartOptions(
                      avgPiecesByService?.avgRevenuePiecesRegular || dummyChart
                    )}
                    series={
                      createLineChartOptions(
                        avgPiecesByService?.avgRevenuePiecesRegular ||
                          dummyChart
                      ).series
                    }
                    type="line"
                    height={300}
                    width="100%"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card bg-white p-4" style={{ position: "relative" }}>
              {isLoadingAvgWeightPerPieces && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "white",
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WidgetSkeleton />
                </div>
              )}

              <div className="row mb-2">
                <div className="col-12">
                  <div className="flex justify-between">
                    <span className="text-black">AVG. WEIGHT PER PIECES</span>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div
                    className="table-responsive"
                    style={{ maxHeight: "300px", minHeight: "300px" }}
                  >
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ backgroundColor: "white" }}></th>
                          {safeAvgWeightPerPieces.labels.map(
                            (month: string, idx: number) => (
                              <th
                                key={idx}
                                style={{
                                  color: "#666",
                                  fontWeight: "normal",
                                }}
                              >
                                {month}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Premium Services */}
                        <tr style={{ backgroundColor: "#4a0f0f" }}>
                          <td
                            colSpan={safeAvgWeightPerPieces.labels.length + 1}
                            style={{
                              backgroundColor: "#4a0f0f",
                              color: "#ffffff",
                              padding: "8px 12px",
                              fontWeight: 500,
                            }}
                          >
                            Premium Services
                          </td>
                        </tr>
                        {safeAvgWeightPerPieces.avgWeightPerPiecesPremium.map(
                          (service: any, sIdx: number) => (
                            <tr key={sIdx}>
                              <td style={{ color: "#666" }}>{service.name}</td>
                              {service.data.map((val: number, vIdx: number) => (
                                <td key={vIdx} style={{ color: "#666" }}>
                                  {val.toFixed(1)} Kg
                                </td>
                              ))}
                            </tr>
                          )
                        )}

                        {/* Regular Services */}
                        <tr style={{ backgroundColor: "#4a0f0f" }}>
                          <td
                            colSpan={safeAvgWeightPerPieces.labels.length + 1}
                            style={{
                              backgroundColor: "#4a0f0f",
                              color: "#ffffff",
                              padding: "8px 12px",
                              fontWeight: 500,
                            }}
                          >
                            Regular Services
                          </td>
                        </tr>
                        {safeAvgWeightPerPieces.avgWeightPerPiecesRegular.map(
                          (service: any, sIdx: number) => (
                            <tr key={sIdx}>
                              <td style={{ color: "#666" }}>{service.name}</td>
                              {service.data.map((val: number, vIdx: number) => (
                                <td key={vIdx} style={{ color: "#666" }}>
                                  {val.toFixed(1)} Kg
                                </td>
                              ))}
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Section7;
